/**
 * ConcoursManager - Main component for PKA Ton Concours management
 * Dashboard for listing, creating, editing, and managing concours
 */
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Edit,
  Eye,
  FileText,
  Filter,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Star,
  Trash2,
  TrendingUp,
  Upload,
  Zap
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '../../services/api';
import { concoursApiClient } from '../../services/concoursApi';
import {
  Concours,
  CONCOURS_CATEGORIES,
  CONCOURS_LEVELS,
  CONCOURS_STATUS_COLORS,
  CONCOURS_STATUS_LABELS,
  ConcoursCategory,
  ConcoursLevel,
  ConcoursListParams,
  ConcoursStatus,
} from '../../types/concours';
import BulkExtract from './BulkExtract';
import ConcoursDetail from './ConcoursDetail';
import ConcoursForm from './ConcoursForm';
import ConcoursStats from './ConcoursStats';

type ViewMode = 'list' | 'create' | 'edit' | 'detail' | 'bulk-extract';

const ConcoursManager: React.FC = () => {
  // State
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [concoursList, setConcoursList] = useState<Concours[]>([]);
  const [selectedConcours, setSelectedConcours] = useState<Concours | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConcoursStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<ConcoursCategory | ''>('');
  const [levelFilter, setLevelFilter] = useState<ConcoursLevel | ''>('');
  const [yearFilter, setYearFilter] = useState<number | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Stats
  const [showStats, setShowStats] = useState(false);

  // Import / Export
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; skipped: number; failed: number } | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const res = await authFetch(`${apiBase}/api/pka/concours/export`);
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : 'concours_export.json';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImportLoading(true);
    setImportResult(null);
    setError(null);
    try {
      const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch(`${apiBase}/api/pka/concours/import?on_conflict=skip`, { method: 'POST', body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `Erreur ${res.status}`);
      setImportResult(body.data);
      fetchConcours();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'import');
    } finally {
      setImportLoading(false);
    }
  };

  // Fetch concours list
  const fetchConcours = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ConcoursListParams = {
        page: currentPage,
        limit: itemsPerPage,
        sort_by: 'updated_at',
        sort_order: 'desc',
      };

      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (levelFilter) params.level = levelFilter;
      if (yearFilter) params.year = yearFilter;

      const response = await concoursApiClient.listConcours(params);

      setConcoursList(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des concours');
      setConcoursList([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, categoryFilter, levelFilter, yearFilter]);

  useEffect(() => {
    fetchConcours();
  }, [fetchConcours]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchConcours();
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setCategoryFilter('');
    setLevelFilter('');
    setYearFilter('');
    setCurrentPage(1);
  };

  const handleView = (concours: Concours) => {
    setSelectedConcours(concours);
    setViewMode('detail');
  };

  const handleEdit = (concours: Concours) => {
    setSelectedConcours(concours);
    setViewMode('edit');
  };

  const handleDelete = async (concours: Concours) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${concours.title}" ?`)) {
      return;
    }

    try {
      await concoursApiClient.deleteConcours(concours.id);
      fetchConcours();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (concours: Concours) => {
    const newYear = new Date().getFullYear() + 1;
    const newSlug = concoursApiClient.generateSlug(concours.title, newYear);

    try {
      await concoursApiClient.duplicateConcours(concours.id, {
        new_slug: newSlug,
        new_year: newYear,
        status: 'draft',
      });
      fetchConcours();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la duplication');
    }
  };

  const handlePublish = async (concours: Concours) => {
    try {
      await concoursApiClient.publishConcours(concours.id);
      fetchConcours();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la publication');
    }
  };

  const handleArchive = async (concours: Concours) => {
    try {
      await concoursApiClient.archiveConcours(concours.id);
      fetchConcours();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'archivage');
    }
  };

  const handleFormSuccess = () => {
    setViewMode('list');
    setSelectedConcours(null);
    fetchConcours();
  };

  const handleFormCancel = () => {
    setViewMode('list');
    setSelectedConcours(null);
  };

  // Render list view
  const renderList = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">PKA Ton Concours</h1>
          <p className="text-gray-600">Gestion des concours administratifs</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowStats(!showStats)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${showStats
              ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <TrendingUp className="w-4 h-4" />
            Statistiques
          </button>
          {/* Export */}
          <button
            onClick={handleExport}
            disabled={exportLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition"
          >
            {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Exporter
          </button>
          {/* Import */}
          <button
            onClick={() => importInputRef.current?.click()}
            disabled={importLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition"
          >
            {importLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importer
          </button>
          {/* Bulk Extract */}
          <button
            onClick={() => setViewMode('bulk-extract')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition"
          >
            <Zap className="w-4 h-4" />
            Extraction bulk
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleImport}
          />
          <button
            onClick={() => setViewMode('create')}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Plus className="w-4 h-4" />
            Nouveau Concours
          </button>
        </div>
      </div>

      {/* Stats Panel */}
      {showStats && <ConcoursStats />}

      {/* Import result banner */}
      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="text-sm text-green-800">
            <span className="font-semibold">Import terminé — </span>
            {importResult.created} créé(s), {importResult.updated} mis à jour, {importResult.skipped} ignoré(s), {importResult.failed} erreur(s).
          </div>
          <button onClick={() => setImportResult(null)} className="text-green-600 hover:text-green-800 text-xs font-medium flex-shrink-0">✕</button>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un concours..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition ${showFilters
              ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
          >
            <Filter className="w-4 h-4" />
            Filtres
          </button>
          <button
            type="button"
            onClick={fetchConcours}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </form>

        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ConcoursStatus | '')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Tous les statuts</option>
                {Object.entries(CONCOURS_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as ConcoursCategory | '')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Toutes les catégories</option>
                {CONCOURS_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value as ConcoursLevel | '')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Tous les niveaux</option>
                {CONCOURS_LEVELS.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Toutes les années</option>
                {[2024, 2025, 2026, 2027].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                Réinitialiser les filtres
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-gray-600">
        {totalItems} concours trouvé{totalItems > 1 ? 's' : ''}
      </div>

      {/* Concours List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      ) : concoursList.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun concours trouvé</h3>
          <p className="text-gray-500 mb-4">
            {searchQuery || statusFilter || categoryFilter || levelFilter || yearFilter
              ? 'Modifiez vos filtres pour voir plus de résultats'
              : 'Commencez par créer votre premier concours'}
          </p>
          <button
            onClick={() => setViewMode('create')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Plus className="w-4 h-4" />
            Créer un concours
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Concours</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Catégorie</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Niveau</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Statut</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Vues</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {concoursList.map((concours) => (
                <tr key={concours.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{concours.title}</h3>
                          {concours.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                        </div>
                        <p className="text-sm text-gray-500">{concours.institution} • {concours.year}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-700">{concours.category}</span>
                    {concours.subcategory && (
                      <span className="text-sm text-gray-400 block">{concours.subcategory}</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-700">{concours.level}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CONCOURS_STATUS_COLORS[concours.status]}`}>
                      {CONCOURS_STATUS_LABELS[concours.status]}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm text-gray-600">{concours.view_count || 0}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleView(concours)}
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                        title="Voir"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(concours)}
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(concours)}
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                        title="Dupliquer"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(concours)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Page {currentPage} sur {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Précédent
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Main render
  return (
    <div className="min-h-screen">
      {viewMode === 'list' && renderList()}
      {viewMode === 'create' && (
        <ConcoursForm
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
      {viewMode === 'edit' && selectedConcours && (
        <ConcoursForm
          concours={selectedConcours}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}
      {viewMode === 'detail' && selectedConcours && (
        <ConcoursDetail
          concours={selectedConcours}
          onEdit={() => setViewMode('edit')}
          onBack={() => setViewMode('list')}
          onPublish={() => handlePublish(selectedConcours)}
          onArchive={() => handleArchive(selectedConcours)}
        />
      )}
      {viewMode === 'bulk-extract' && (
        <BulkExtract
          onBack={() => setViewMode('list')}
          onDraftsCreated={() => { setViewMode('list'); fetchConcours(); }}
        />
      )}
    </div>
  );
};

export default ConcoursManager;
